import {
  AlignmentType,
  BorderStyle,
  convertInchesToTwip,
  Document,
  HeadingLevel,
  LineRuleType,
  Packer,
  PageBreak,
  Paragraph,
  TextRun,
} from "docx";
import { formatUkDateSafe } from "../../../Helpers/Dates";
import type { ImprovementDetail, ImproveTaskDetail } from "../../../Types/Improve/ImprovementPlanDetail";
import {
  ImproveAreaExportData,
  IMPROVE_EXPORT_COLS,
  ImproveExportFieldVisibility,
  ImproveExportOptions,
  fullImproveExportFieldVisibility,
} from "./types";
import {
  assignedToNames,
  formatUrlCell,
  taskAssigneeName,
  tagsJoin,
} from "./improveExportHelpers";

const FONT = "Arial";
const COLOR_BLACK = "000000";
const SIZE_BODY_HALF_PT = 24;
const SIZE_SECTION_TITLE_HALF_PT = 28;
const SIZE_AREA_TITLE_HALF_PT = 32;

const paragraphJustified = {
  alignment: AlignmentType.JUSTIFIED,
} as const;

function paragraphSpacing(opts: { before?: number; after?: number } = {}) {
  return {
    line: 360,
    lineRule: LineRuleType.AUTO,
    ...opts,
  };
}

function bodyRun(text: string, bold?: boolean): TextRun {
  return new TextRun({
    text,
    font: FONT,
    size: SIZE_BODY_HALF_PT,
    color: COLOR_BLACK,
    bold: bold === true ? true : undefined,
  });
}

function sizedBoldRun(text: string, sizeHalfPt: number): TextRun {
  return new TextRun({
    text,
    font: FONT,
    size: sizeHalfPt,
    color: COLOR_BLACK,
    bold: true,
  });
}

function labelValueParagraph(label: string, value: string): Paragraph {
  const display = value.trim().length > 0 ? value : "—";
  return new Paragraph({
    ...paragraphJustified,
    children: [bodyRun(`${label}: `, true), bodyRun(display)],
    spacing: paragraphSpacing({ after: 120 }),
  });
}

function planParagraphs(
  area: ImproveAreaExportData,
  vis: ImproveExportFieldVisibility
): Paragraph[] {
  const out: Paragraph[] = [];
  const title = area.title ?? "";

  if (vis["Plan title"]) {
    out.push(
      new Paragraph({
        ...paragraphJustified,
        heading: HeadingLevel.HEADING_1,
        children: [
          sizedBoldRun(title.length > 0 ? title : "Untitled", SIZE_AREA_TITLE_HALF_PT),
        ],
        spacing: paragraphSpacing({ after: 480 }),
        border: {
          bottom: {
            color: COLOR_BLACK,
            style: BorderStyle.SINGLE,
            size: 12,
            space: 8,
          },
        },
      })
    );
  }
  if (vis["Due date"]) {
    out.push(labelValueParagraph("Due date", formatUkDateSafe(area.endDate)));
  }
  if (vis.Owner) {
    out.push(labelValueParagraph("Owner", area.ownerName ?? ""));
  }
  if (vis["Plan status"]) {
    out.push(labelValueParagraph("Plan status", area.status ?? ""));
  }
  return out;
}

function improvementParagraphs(
  imp: ImprovementDetail,
  vis: ImproveExportFieldVisibility
): Paragraph[] {
  const out: Paragraph[] = [];
  const objTitle = imp.title?.trim() || "Untitled objective";

  if (
    vis.Objective ||
    vis["Improvement status"] ||
    vis["Assigned to"] ||
    vis["Target date"] ||
    vis["Completion date"] ||
    vis["Expected impact"] ||
    vis["Actual impact"] ||
    vis.Tags ||
    vis["Custom tags"] ||
    vis["Custom date"] ||
    vis["Column A"] ||
    vis["Column B"] ||
    vis["Column C"] ||
    vis["URL 1"] ||
    vis["URL 2"] ||
    vis["URL 3"]
  ) {
    out.push(
      new Paragraph({
        ...paragraphJustified,
        heading: HeadingLevel.HEADING_2,
        children: [
          sizedBoldRun(
            vis.Objective ? `Objective: ${objTitle}` : "Objective",
            SIZE_SECTION_TITLE_HALF_PT
          ),
        ],
        spacing: paragraphSpacing({ before: 280, after: 360 }),
      })
    );
  }

  if (vis["Improvement status"]) {
    out.push(labelValueParagraph("Status", imp.status ?? ""));
  }
  if (vis["Assigned to"]) {
    out.push(labelValueParagraph("Assigned to", assignedToNames(imp.assignedTo)));
  }
  if (vis["Target date"]) {
    out.push(labelValueParagraph("Target date", formatUkDateSafe(imp.targetDate)));
  }
  if (vis["Completion date"]) {
    out.push(
      labelValueParagraph("Completion date", formatUkDateSafe(imp.completionDate))
    );
  }
  if (vis["Expected impact"]) {
    out.push(labelValueParagraph("Expected impact", imp.expectedImpact ?? ""));
  }
  if (vis["Actual impact"]) {
    out.push(labelValueParagraph("Actual impact", imp.actualImpact ?? ""));
  }
  if (vis.Tags) {
    const t = tagsJoin(imp.tags);
    if (t) out.push(labelValueParagraph("Tags", t));
  }
  if (vis["Custom tags"]) {
    out.push(labelValueParagraph("Custom tags", imp.customTags ?? ""));
  }
  if (vis["Custom date"]) {
    out.push(labelValueParagraph("Custom date", formatUkDateSafe(imp.customDate)));
  }
  if (vis["Column A"]) {
    out.push(labelValueParagraph("Column A", imp.customTextA ?? ""));
  }
  if (vis["Column B"]) {
    out.push(labelValueParagraph("Column B", imp.customTextB ?? ""));
  }
  if (vis["Column C"]) {
    out.push(labelValueParagraph("Column C", imp.customTextC ?? ""));
  }
  if (vis["URL 1"]) {
    out.push(
      labelValueParagraph("URL 1", formatUrlCell(imp.uRL1, imp.uRL1Text))
    );
  }
  if (vis["URL 2"]) {
    out.push(
      labelValueParagraph("URL 2", formatUrlCell(imp.uRL2, imp.uRL2Text))
    );
  }
  if (vis["URL 3"]) {
    out.push(
      labelValueParagraph("URL 3", formatUrlCell(imp.uRL3, imp.uRL3Text))
    );
  }

  return out;
}

function taskParagraphs(
  task: ImproveTaskDetail,
  vis: ImproveExportFieldVisibility
): Paragraph[] {
  const out: Paragraph[] = [];
  const heading = task.taskHeading?.trim() || "Task";

  const anyTaskVis =
    vis["Task heading"] ||
    vis["Task status"] ||
    vis["Task target date"] ||
    vis["Task completion date"] ||
    vis["Task assigned to"] ||
    vis["Column D"] ||
    vis["Column E"] ||
    vis["Column F"] ||
    vis["Task custom tags"];

  if (!anyTaskVis) return out;

  out.push(
    new Paragraph({
      ...paragraphJustified,
      heading: HeadingLevel.HEADING_3,
      children: [
        sizedBoldRun(vis["Task heading"] ? heading : "Task", SIZE_BODY_HALF_PT + 4),
      ],
      spacing: paragraphSpacing({ before: 200, after: 280 }),
    })
  );

  if (vis["Task status"]) {
    out.push(labelValueParagraph("Task status", task.taskStatus ?? ""));
  }
  if (vis["Task target date"]) {
    out.push(labelValueParagraph("Target date", formatUkDateSafe(task.targetDate)));
  }
  if (vis["Task completion date"]) {
    out.push(
      labelValueParagraph("Completion date", formatUkDateSafe(task.completionDate))
    );
  }
  if (vis["Task assigned to"]) {
    out.push(labelValueParagraph("Assigned to", taskAssigneeName(task)));
  }
  if (vis["Column D"]) {
    out.push(labelValueParagraph("Column D", task.customTextD ?? ""));
  }
  if (vis["Column E"]) {
    out.push(labelValueParagraph("Column E", task.customTextE ?? ""));
  }
  if (vis["Column F"]) {
    out.push(labelValueParagraph("Column F", task.customTextF ?? ""));
  }
  if (vis["Task custom tags"]) {
    out.push(labelValueParagraph("Custom tags", task.customTagsB ?? ""));
  }

  return out;
}

function buildAreaParagraphs(
  area: ImproveAreaExportData,
  vis: ImproveExportFieldVisibility
): Paragraph[] {
  const out: Paragraph[] = [...planParagraphs(area, vis)];
  const improvements = area.improvements ?? [];

  for (const imp of improvements) {
    out.push(...improvementParagraphs(imp, vis));
    const tasks = imp.tasks?.length ? imp.tasks : [];
    if (tasks.length === 0) {
      /* keep improvement block only */
    } else {
      for (const task of tasks) {
        out.push(...taskParagraphs(task, vis));
      }
    }
  }

  return out;
}

export async function improveDocxExporter(
  areas: ImproveAreaExportData[],
  _baseFilename: string,
  options?: ImproveExportOptions
): Promise<Blob> {
  if (areas.length === 0) {
    throw new Error("No areas to export.");
  }

  const vis = fullImproveExportFieldVisibility(options?.fieldVisibility);
  if (!IMPROVE_EXPORT_COLS.some((k) => vis[k] === true)) {
    throw new Error("No export columns selected.");
  }

  const children: Paragraph[] = [];
  areas.forEach((area, index) => {
    if (index > 0) {
      children.push(
        new Paragraph({
          ...paragraphJustified,
          spacing: paragraphSpacing(),
          children: [new PageBreak()],
        })
      );
    }
    children.push(...buildAreaParagraphs(area, vis));
  });

  const doc = new Document({
    sections: [{ children }],
  });

  return Packer.toBlob(doc);
}
